import { useCallback, useEffect, useReducer, useRef, useState, type ChangeEvent } from 'react';
import {
  importCv,
  ImportApiError,
  listPoolMembershipsForPerson,
  type ImportResult,
  type PersonPoolMembership,
} from '../services/candidatePoolsApi';
import { profileUpdateApi, type CvIngestResult } from '../services/profileUpdateApi';
import type { ViewerContext } from '../types/profileViewerContext';
import {
  INITIAL_IMPORT_STATE,
  classifyImportError,
  importReducer,
  type ImportSuccessSummary,
} from '../components/CandidatePools/importState';
import {
  pickWritablePoolForPerson,
  usesPoolCvImport,
  usesSelfServiceCvImport,
} from '../utils/profileCvUpload';
import { useAuth } from './useAuth';

const UPLOAD_TO_PARSING_MS = 1500;
const PARSING_TO_SAVING_MS = 11_000;
const MAX_FILE_MB = 15;

function buildPoolSuccessSummary(result: ImportResult): ImportSuccessSummary {
  const p = result.persist;
  const applied = p?.applied ?? {};
  return {
    person_id: result.person_id,
    name: applied.name ?? null,
    title: applied.title ?? null,
    primary_location: applied.primary_location ?? null,
    seniority_level: applied.seniority_level ?? null,
    years_experience_total: applied.years_experience_total ?? null,
    experience_rows: p?.experience_rows ?? 0,
    project_rows: p?.project_rows ?? 0,
    skill_rows: p?.skill_rows ?? 0,
    skill_phrase_rows: p?.skill_phrase_rows ?? 0,
    language_rows: p?.language_rows ?? 0,
    review_status: p?.review_status ?? 'unreviewed',
    parser_version: p?.parser_version ?? 'unknown',
  };
}

function buildSelfServiceSuccessSummary(result: CvIngestResult): ImportSuccessSummary {
  return {
    person_id: result.person_id,
    name: null,
    title: null,
    primary_location: null,
    seniority_level: null,
    years_experience_total: null,
    experience_rows: result.summary.experience,
    project_rows: 0,
    skill_rows: result.summary.skills,
    skill_phrase_rows: 0,
    language_rows: result.summary.languages,
    review_status: result.review_status,
    parser_version: result.parser_version,
  };
}

export interface UsePersonCvUploadOptions {
  personId: number | null | undefined;
  viewerContext: ViewerContext;
  onImported: () => void | Promise<void>;
  /** When false, skip pool-membership prefetch (parent supplies cvUpload). */
  enabled?: boolean;
}

export function usePersonCvUpload({
  personId,
  viewerContext,
  onImported,
  enabled = true,
}: UsePersonCvUploadOptions) {
  const authToken = useAuth((s) => s.token);
  const [importState, dispatchImport] = useReducer(importReducer, INITIAL_IMPORT_STATE);
  const [poolMemberships, setPoolMemberships] = useState<PersonPoolMembership[]>([]);
  const [poolLoadError, setPoolLoadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const importAbortRef = useRef<AbortController | null>(null);
  const importStepTimersRef = useRef<number[]>([]);
  const elapsedTimerRef = useRef<number | null>(null);
  const lastFileRef = useRef<File | null>(null);

  const writablePoolId =
    personId != null ? pickWritablePoolForPerson(poolMemberships, personId) : null;
  const poolImport = usesPoolCvImport(viewerContext);
  const selfImport = usesSelfServiceCvImport(viewerContext);

  const profileUpdateJwt =
    viewerContext.kind === 'self_magic'
      ? viewerContext.jwt
      : selfImport
        ? authToken
        : null;
  const profileUpdateToken =
    viewerContext.kind === 'self_magic' ? viewerContext.token : null;

  useEffect(() => {
    if (!enabled || !poolImport || personId == null) {
      setPoolMemberships([]);
      return;
    }
    let cancelled = false;
    setPoolLoadError(null);
    listPoolMembershipsForPerson(personId)
      .then((rows) => {
        if (!cancelled) setPoolMemberships(rows);
      })
      .catch(() => {
        if (!cancelled) setPoolLoadError('Could not load talent pools for this person.');
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, poolImport, personId]);

  const clearStepTimers = useCallback(() => {
    importStepTimersRef.current.forEach((id) => window.clearTimeout(id));
    importStepTimersRef.current = [];
    if (elapsedTimerRef.current != null) {
      window.clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearStepTimers();
      importAbortRef.current?.abort();
    };
  }, [clearStepTimers]);

  const resetImport = useCallback(() => {
    clearStepTimers();
    importAbortRef.current?.abort();
    importAbortRef.current = null;
    lastFileRef.current = null;
    dispatchImport({ type: 'reset' });
  }, [clearStepTimers]);

  const openFilePicker = useCallback(() => {
    if (importState.phase !== 'idle' && importState.phase !== 'success' && importState.phase !== 'error') {
      return;
    }
    fileInputRef.current?.click();
  }, [importState.phase]);

  const uploadFile = useCallback(
    async (file: File) => {
      if (personId == null) return;
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        dispatchImport({
          type: 'fail',
          error: classifyImportError(`File is larger than ${MAX_FILE_MB}MB.`, 400),
        });
        return;
      }
      if (poolImport && writablePoolId == null) {
        dispatchImport({
          type: 'fail',
          error: classifyImportError(
            'Add this person to a talent pool you can edit, or send a profile-update link.',
            403
          ),
        });
        return;
      }
      if (selfImport && !profileUpdateJwt) {
        dispatchImport({
          type: 'fail',
          error: classifyImportError('Sign in to upload a CV.', 401),
        });
        return;
      }

      clearStepTimers();
      importAbortRef.current?.abort();
      const controller = new AbortController();
      importAbortRef.current = controller;
      lastFileRef.current = file;

      const startedAt = performance.now();
      dispatchImport({
        type: 'start',
        mode: 'cv',
        fileName: file.name,
        startedAt,
      });

      elapsedTimerRef.current = window.setInterval(() => {
        dispatchImport({ type: 'tick', now: performance.now() });
      }, 500);

      importStepTimersRef.current.push(
        window.setTimeout(() => dispatchImport({ type: 'advance_to_parsing' }), UPLOAD_TO_PARSING_MS)
      );
      importStepTimersRef.current.push(
        window.setTimeout(
          () => dispatchImport({ type: 'advance_to_saving' }),
          UPLOAD_TO_PARSING_MS + PARSING_TO_SAVING_MS
        )
      );

      try {
        if (poolImport && writablePoolId != null) {
          const result = await importCv(writablePoolId, file, {
            signal: controller.signal,
            personId,
          });
          clearStepTimers();
          dispatchImport({ type: 'success', summary: buildPoolSuccessSummary(result) });
        } else if (profileUpdateJwt) {
          const result = await profileUpdateApi.uploadCv(file, profileUpdateToken, profileUpdateJwt);
          clearStepTimers();
          dispatchImport({ type: 'success', summary: buildSelfServiceSuccessSummary(result) });
        } else {
          throw new Error('CV upload is not available for this viewer.');
        }
        await onImported();
      } catch (err: unknown) {
        clearStepTimers();
        const status = err instanceof ImportApiError ? err.status : undefined;
        dispatchImport({ type: 'fail', error: classifyImportError(err, status) });
      }
    },
    [
      personId,
      poolImport,
      selfImport,
      writablePoolId,
      profileUpdateJwt,
      profileUpdateToken,
      clearStepTimers,
      onImported,
    ]
  );

  const onFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (file) void uploadFile(file);
    },
    [uploadFile]
  );

  const retryUpload = useCallback(() => {
    const file = lastFileRef.current;
    if (!file) return;
    resetImport();
    void uploadFile(file);
  }, [resetImport, uploadFile]);

  const busy =
    importState.phase === 'uploading' ||
    importState.phase === 'parsing' ||
    importState.phase === 'saving';

  const cancelImport = useCallback(() => {
    importAbortRef.current?.abort();
    clearStepTimers();
    dispatchImport({ type: 'cancel' });
  }, [clearStepTimers]);

  return {
    fileInputRef,
    openFilePicker,
    onFileInputChange,
    importState,
    busy,
    resetImport,
    retryUpload,
    cancelImport,
    writablePoolId,
    poolLoadError,
    canUploadCv: poolImport ? writablePoolId != null : selfImport ? !!profileUpdateJwt : false,
  };
}

export type UsePersonCvUploadResult = ReturnType<typeof usePersonCvUpload>;
